import { WmButton, WmOption, WmSelect, WmFlyout } from "@watermarkinsights/ripple-react";
import { AppContext, AppContextInterface, OutcomesTerminology } from "../../contexts/AppContext";
import React, { useContext, useEffect } from "react";
import { useReducer } from "react";
import translate from "../../i18n/translate";
import { closeFlyout } from "../../static/js/commons";
import { useIntl } from "react-intl";
import { useFormatMessage } from "../common/helper";
import {
  NoResults,
  Table,
  TableBody,
  TableHeader,
  formatDateFullYear,
} from "../common/helper";
import { DrilldownFilters } from "../common/interfaces/Interface";

const ORGANIZATION_MISSION = "organization_mission";
const ORGANIZATION_LEARNING_OUTCOME = "organization_learning_outcome";
const ORGANIZATION_SUCCESS_OUTCOME = "organization_success_outcome";
const ORGANIZATION_ILO_MAPPING = "organization_ilo_mapping";

const PROGRAM_ILO = "programILO";
const ASSESSMENT_PLANNING_ORGANIZATIONS = "assessment_planning_organizations";
const ASSESSMENT_PLANNING_PROGRAMS = "assessment_planning_programs";

const PROGRAM_MISSION = "program_mission";
const PROGRAM_LEARNING_OUTCOMES = "program_learning_outcomes";
const PROGRAM_SUCCESS_OUTCOMES = "program_success_outcomes";
const PROGRAM_CMAP = "program_cmap";
const PROGRAM_ILO_MAPPING = "program_ilo_mapping";

const COURSE_LEARNING_OUTCOMES = "course_learning_outcomes";
const COURSE_ILO_MAPPING = "course_ilo_mapping";

const getDrilldownName = (type: string, formatMessage: (descriptor: { id: string }, values?: Record<string, any>) => string, outcomesTerminology?: OutcomesTerminology | null) => {
  switch (type) {
    case ORGANIZATION_MISSION:
      return formatMessage({ id: "app.drilldown.missionStatementByOrg" });
    case ORGANIZATION_LEARNING_OUTCOME:
      return formatMessage(
        { id: "app.drilldown.learningOutcomes" },
        { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
      );
    case ORGANIZATION_SUCCESS_OUTCOME:
      return formatMessage(
        { id: "app.drilldown.successOutcomes" },
        { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
      );
    case ORGANIZATION_ILO_MAPPING:
      return formatMessage({ id: "app.drilldown.ILOMapping" });
    case PROGRAM_MISSION:
      return formatMessage({ id: "app.drilldown.missionStatementByProgram" });
    case PROGRAM_LEARNING_OUTCOMES:
      return formatMessage(
        { id: "app.drilldown.learningOutcomesByProgram" },
        { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
      );
    case PROGRAM_SUCCESS_OUTCOMES:
      return formatMessage(
        { id: "app.drilldown.successOutcomesByProgram" },
        { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
      );
    case PROGRAM_CMAP:
      return formatMessage({ id: "app.drilldown.curriculumMap" });
    case PROGRAM_ILO_MAPPING:
      return formatMessage({ id: "app.drilldown.ILOMappingByProgram" });
    case PROGRAM_ILO:
      return formatMessage({ id: "app.programILOMapping.programsMappedILO" });
    case COURSE_LEARNING_OUTCOMES:
      return formatMessage(
        { id: "app.drilldown.learningOutcomesByCourse" },
        { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
      );
    case COURSE_ILO_MAPPING:
      return formatMessage({ id: "app.drilldown.ILOMappingByCourse" });
    case ASSESSMENT_PLANNING_ORGANIZATIONS:
      return formatMessage({ id: "app.drilldown.ParticipatingOrgByOrg" });
    case ASSESSMENT_PLANNING_PROGRAMS:
      return formatMessage({ id: "app.drilldown.ParticipatingOrgByProgram" });
    default:
      return "";
  }
};

const getDrilldownColumns = (segment: string, formatMessage: (descriptor: { id: string }, values?: Record<string, any>) => string, outcomesTerminology?: OutcomesTerminology | null) => {
  if (
    [
      ORGANIZATION_MISSION,
      PROGRAM_MISSION,
      PROGRAM_CMAP,
      ASSESSMENT_PLANNING_ORGANIZATIONS,
      ASSESSMENT_PLANNING_PROGRAMS,
    ].includes(segment)
  ) {
    return [
      translate("app.drilldown.organization"),
      translate("app.drilldown.status"),
      translate("app.drilldown.lastUpdated"),
      translate("app.drilldown.lead"),
    ];
  }

  if (
    [
      ORGANIZATION_LEARNING_OUTCOME,
      ORGANIZATION_SUCCESS_OUTCOME,
      PROGRAM_LEARNING_OUTCOMES,
      PROGRAM_SUCCESS_OUTCOMES,
    ].includes(segment)
  ) {
    return [
      translate("app.drilldown.organization"),
      translate("app.drilldown.status"),
      formatMessage(
        { id: "app.drilldown.numberOfOutcomes" },
        { outcome_plural: outcomesTerminology?.outcomes?.plural || "..." }
      ),
      translate("app.drilldown.lead"),
    ];
  }

  if ([ORGANIZATION_ILO_MAPPING, PROGRAM_ILO_MAPPING].includes(segment)) {
    return [
      translate("app.drilldown.organization"),
      translate("app.drilldown.status"),
      translate("app.drilldown.ILOMappedTo"),
      translate("app.drilldown.lead"),
    ];
  }

  if (PROGRAM_ILO === segment) {
    return [
      translate("app.drilldown.organization"),
      translate("app.drilldown.status"),
      formatMessage(
        { id: "app.drilldown.outcomesMapped" },
        { outcome_plural: outcomesTerminology?.outcomes?.plural || "..." }
      ),
      translate("app.drilldown.lead"),
    ];
  }

  return [];
};

const filterHierarchyByMode = (
  drilldownType: string,
  status: string,
  completeHierarchy: any
) => {
  if (
    [ASSESSMENT_PLANNING_ORGANIZATIONS, ASSESSMENT_PLANNING_PROGRAMS].includes(
      drilldownType
    ) ||
    !Array.isArray(completeHierarchy)
  ) {
    const hierarchy = filterHierarchyByStatus(status, completeHierarchy);
    return hierarchy != null ? hierarchy : {};
  } else {
    return completeHierarchy
      .map((node: any) => {
        return filterHierarchyByStatus(status, node);
      })
      .filter((node: any) => {
        return node != null;
      });
  }
};

const filterHierarchyByStatus = (status: string, completeHierarchy: any) => {
  if (status === "any") {
    return completeHierarchy;
  } else {
    const hierarchy = filterHierarchy(completeHierarchy, status);
    return hierarchy ? hierarchy : null;
  }
};

const filterHierarchy = (node: any, status: string) => {
  if (node.showStats && node.status === status) {
    return node;
  } else if (node.showStats && node.status !== status) {
    return null;
  } else {
    const children = node.children
      .map((child: any) => {
        return filterHierarchy(child, status);
      })
      .filter((node: any) => {
        return node != null;
      });

    if (children.length > 0) {
      return {
        ...node,
        children: children,
      };
    } else {
      return null;
    }
  }
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case "filterHierarchyByStatus":
      const filteredHierarchy = filterHierarchyByMode(
        state.drilldownType,
        state.selectedStatus,
        state.completeHierarchy
      );
      return {
        ...state,
        hierarchy: filteredHierarchy,
        showTable: action.showTable,
      };
    case "setSelectedStatus":
      return {
        ...state,
        selectedStatus: action.status,
      };
    case "setCompleteHierarchy":
      return {
        ...state,
        completeHierarchy: action.data,
      };
    case "setLeads":
      return {
        ...state,
        leads: action.leads,
      };
    default:
      return state;
  }
};

export interface DrilldownViewProps {
  setIsDrilldownOpen: (arg0: boolean) => void;
  drilldownName?: string;
  drilldownType: string;
  drilldownStatus: string;
  total: string | JSX.Element;
  showTable: boolean;
  completeHierarchy: any;
  filters?: DrilldownFilters[] | null;
  subHeader?: string;
  title?: string;
  leads: any;
  planStatus?: number;
  isVisible?: boolean;
  open?: boolean;
  id?: string;
}

export default function OpenDrillDownView({
  setIsDrilldownOpen,
  drilldownName,
  drilldownType,
  drilldownStatus,
  total,
  showTable,
  completeHierarchy,
  filters,
  subHeader,
  title,
  leads,
  planStatus,
  isVisible = false,
  open = false,
  id = "drill-down-view",
}: DrilldownViewProps) {
  const initialState = {
    hierarchy: null,
    drilldownType: drilldownType,
    selectedStatus: drilldownStatus,
    completeHierarchy: null,
    showTable: Boolean,
    leads: leads,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const formatMessage = useFormatMessage();

  const CloseDrillDownView = () => {
    setIsDrilldownOpen(false);
    closeFlyout();
  };
  const { institution, outcomesTerminology } =
    useContext<AppContextInterface>(AppContext);

  const statusChangeHandler = (status: string) => {
    dispatch({ type: "setSelectedStatus", status: status });
  };

  useEffect(() => {
    state.completeHierarchy &&
      dispatch({ type: "filterHierarchyByStatus", showTable: showTable });
  }, [state.selectedStatus, state.completeHierarchy]);
  useEffect(() => {
    dispatch({ type: "setCompleteHierarchy", data: completeHierarchy });
  }, [completeHierarchy]);
  useEffect(() => {
    dispatch({ type: "setLeads", leads: leads });
  }, [leads]);

  const renderHierarchy = (node: any) => {
    return (
      <React.Fragment key={`${node.uuid}-hierarchy`}>
        <Node
          node={node}
          type={drilldownType}
          leads={state.leads}
          planStatus={planStatus}
        />
        {node.children.map((child: any) => {
          return renderHierarchy(child);
        })}
      </React.Fragment>
    );
  };

  const renderHierarchyTable = () => {
    return (
      <Table>
        <TableHeader columns={getDrilldownColumns(drilldownType, formatMessage, outcomesTerminology)} />
        <TableBody>
          {Array.isArray(state.hierarchy)
            ? state.hierarchy.map((node: any) => {
              return renderHierarchy(node);
            })
            : renderHierarchy(state.hierarchy)}
        </TableBody>
      </Table>
    );
  };

  const hierarchyTableBody = () => {
    return (
      <div className="org-table">
        {!Object.keys(state.hierarchy).length ? (
          <NoResults />
        ) : (
          renderHierarchyTable()
        )}
      </div>
    );
  };

  return (
    <WmFlyout
      id={id}
      eyebrow={subHeader}
      heading={String(drilldownName || getDrilldownName(drilldownType, formatMessage, outcomesTerminology) || "No Heading")}
      open={open}
      flyoutWidth="1224px"
      onWmFlyoutCloseTriggered={() => setIsDrilldownOpen(false)}
    >
      <section className="body">
        <div className="flex-row -justified -verticallycentered">
          <div>
            <div className="title -title">
              {title ?? (institution && institution.name)}
            </div>
            <div className="count">{total}</div>
            <div className="count">
              {translate("app.drilldown.leads")}{" "}
              {institution && institution.orgLeads}
            </div>
          </div>
          {state.showTable && filters ? (
            <StatusSelector
              filters={filters}
              changeHandler={statusChangeHandler}
              selected={state.selectedStatus}
            />
          ) : (
            ""
          )}
        </div>
        {state.showTable && state.hierarchy ? (
          hierarchyTableBody()
        ) : (
          <div className="loader -halfscreen">
            <div className="content content-loader">
              {translate("app.loading")}
            </div>
          </div>
        )}
      </section>
    </WmFlyout>
  );
}

const Node = (props: any) => {
  const { node, type, leads, planStatus } = props;
  return (
    <>
      {node.showStats ? (
        <CompleteRow
          node={node}
          type={type}
          lead={leads[node["uuid"]]}
          planStatus={planStatus}
        />
      ) : (
        <NoStatsRow node={node} />
      )}
    </>
  );
};

const CompleteRow = (props: any) => {
  const { node, type, lead, planStatus } = props;
  const leadName = () => {
    if (typeof lead != "undefined") {
      return lead.leads.map((l: any, index: number) => {
        return <div key={`lead-${index}-${node.uuid}`}>{l}</div>;
      });
    }
  };

  const leadEmail = () => {
    if (typeof lead != "undefined") {
      return (
        <a
          className="email_link"
          tabIndex={-1}
          href={`mailto: ${lead.leadEmail}`}
        >
          <WmButton buttonType="textonly" icon="f1ee">
            {translate("app.drilldown.emailLeads")}
          </WmButton>
        </a>
      );
    }
  };

  return (
    <tr className="datarow" key={`${node.uuid}-datarow`}>
      <td className={`datacell level-${node.level}`}>{node.name}</td>
      <StatusCell status={node.status} planStatus={planStatus} />
      <StatsCell type={type} node={node} />
      <td className="datacell">{leadName()}</td>
      <td className="datacell">{leadEmail()}</td>
    </tr>
  );
};

const StatsCell = (props: any) => {
  const { node, type } = props;

  const stats = (type: string) => {
    if (
      [
        ORGANIZATION_MISSION,
        PROGRAM_MISSION,
        PROGRAM_CMAP,
        ASSESSMENT_PLANNING_ORGANIZATIONS,
        ASSESSMENT_PLANNING_PROGRAMS,
      ].includes(type)
    ) {
      return node.updatedAt
        ? formatDateFullYear(node.updatedAt)
        : translate("app.na");
    }

    if (
      [
        ORGANIZATION_LEARNING_OUTCOME,
        ORGANIZATION_SUCCESS_OUTCOME,
        PROGRAM_LEARNING_OUTCOMES,
        PROGRAM_SUCCESS_OUTCOMES,
      ].includes(type)
    ) {
      return node.outcomes;
    }

    if ([ORGANIZATION_ILO_MAPPING, PROGRAM_ILO_MAPPING].includes(type)) {
      return node.iloMapped;
    }

    if (PROGRAM_ILO === type) {
      return `${node.totalMappings} of ${node.totalOutcomes}`;
    }

    return "";
  };

  return <td className="datacell">{stats(type)}</td>;
};

const StatusCell = (props: any) => {
  const content = () => {
    switch (props.status) {
      case "not_mapped":
        return (
          <div className="not-started-status">
            {translate("app.drilldown.notMapped")}
          </div>
        );
      case "not_started":
        return (
          <div className="not-started-status">
            {translate("app.drilldown.notStarted")}
          </div>
        );
      case "mapped":
        return (
          <div className="success-status -mapped">
            <div className="title-text">
              {translate("app.drilldown.mapped")}
            </div>
          </div>
        );
      case "submitted":
        return (
          <div className="success-status -completed">
            <div className="title-text">
              {translate("app.drilldown.submitted")}
            </div>
          </div>
        );
      case "completed":
        return (
          <div className="success-status -completed">
            <div className="title-text">
              {translate("app.drilldown.completed")}
            </div>
          </div>
        );
      case "in_progress":
        let status =
          props.planStatus == 1
            ? translate("app.drilldown.inComplete")
            : translate("app.drilldown.inProgress");
        return (
          <div className="in-progress-status-export">
            <div className="title-text">{status}</div>
          </div>
        );
    }
  };
  return <td className="datacell">{content()}</td>;
};

const NoStatsRow = (props: any) => {
  const { node } = props;
  return (
    <tr className="datarow" key={`${node.uuid}-datarow`}>
      <td className={`datacell level-${node.level}`}>{node.name}</td>
      <td className="datacell"></td>
      <td className="datacell"></td>
      <td className="datacell"></td>
      <td className="datacell"></td>
    </tr>
  );
};

const StatusSelector = (props: any) => {
  const statusText = useIntl().formatMessage({
    id: "app.drilldown.status",
  });
  return (
    <div>
      <WmSelect
        label={statusText}
        labelPosition="left"
        id="select-program-status"
      >
        <WmOption
          onClick={props.changeHandler.bind(null, "any")}
          selected={"any" === props.selected}
        >
          {translate("app.drilldown.any")}
        </WmOption>
        {props.filters.map((filter: any) => {
          return (
            <WmOption
              onClick={props.changeHandler.bind(null, filter.value)}
              selected={filter.value === props.selected}
              key={`status-${filter.value}`}
            >
              {filter.name}
            </WmOption>
          );
        })}
      </WmSelect>
    </div>
  );
};
